import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFilaIntegracaoEvents } from "../../../hooks/useFilaIntegracaoEvents";
import { FILA_INTEGRACAO_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import {
  FilaPorcaoItem,
  filaPorcoesService,
} from "../../../services/filaPorcoesService";
import { BrazilDate } from "../../../utils/BrazilDate";
import { broadcastDashboardRefresh } from "../../../utils/dashboardRefreshBroadcast";
import { PORCAO_COLUNAS, PorcaoStatusColuna } from "../constants/porcoes";

interface ProducaoPorcoesContextData {
  items: FilaPorcaoItem[];
  colunas: Record<PorcaoStatusColuna, FilaPorcaoItem[]>;
  loading: boolean;
  refreshing: boolean;
  actionId: number | null;
  refreshNow: () => Promise<void>;
  handleFinalizar: (id: number) => Promise<void>;
  totalAtivos: number;
}

const ProducaoPorcoesContext = createContext<
  ProducaoPorcoesContextData | undefined
>(undefined);

export const ProducaoPorcoesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const queryClient = useQueryClient();
  const todayISO = useMemo(() => BrazilDate.todayISO(), []);

  useFilaIntegracaoEvents(1, {
    queryKeys: FILA_INTEGRACAO_SSE_QUERY_KEYS.filaPorcoes,
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["fila-porcoes", "producao", todayISO],
    queryFn: () =>
      filaPorcoesService.list({
        data: todayISO,
        page: 1,
        limit: 500,
      }),
    refetchInterval: 30_000,
  });

  const items = useMemo(
    () => (data?.data ?? []).filter((i) => i.status !== "cancelled"),
    [data]
  );

  const finalizarMutation = useMutation({
    mutationFn: (id: number) => filaPorcoesService.finalizar(id),
    onSuccess: () => {
      broadcastDashboardRefresh();
      toast.success("Porção finalizada");
      queryClient.invalidateQueries({ queryKey: ["fila-porcoes"] });
    },
    onError: () => toast.error("Erro ao finalizar porção"),
  });

  const actionId =
    (finalizarMutation.isPending && finalizarMutation.variables) || null;

  const refreshNow = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleFinalizar = useCallback(
    async (id: number) => {
      await finalizarMutation.mutateAsync(id);
    },
    [finalizarMutation]
  );

  const colunas = useMemo(() => {
    const grouped: Record<PorcaoStatusColuna, FilaPorcaoItem[]> = {
      pending: [],
      done: [],
    };
    for (const item of items) {
      if (item.status === "pending" || item.status === "in_progress") {
        grouped.pending.push(item);
      } else if (item.status === "done") {
        grouped.done.push(item);
      }
    }
    return grouped;
  }, [items]);

  const totalAtivos = colunas.pending.length;

  const value = useMemo(
    () => ({
      items,
      colunas,
      loading: isLoading,
      refreshing: isFetching && !isLoading,
      actionId: typeof actionId === "number" ? actionId : null,
      refreshNow,
      handleFinalizar,
      totalAtivos,
    }),
    [
      items,
      colunas,
      isLoading,
      isFetching,
      actionId,
      refreshNow,
      handleFinalizar,
      totalAtivos,
    ]
  );

  return (
    <ProducaoPorcoesContext.Provider value={value}>
      {children}
    </ProducaoPorcoesContext.Provider>
  );
};

export const useProducaoPorcoes = () => {
  const context = useContext(ProducaoPorcoesContext);
  if (!context) {
    throw new Error(
      "useProducaoPorcoes must be used within ProducaoPorcoesProvider"
    );
  }
  return context;
};

export { PORCAO_COLUNAS };
